import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { ArrowLeft, ClipboardCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const base = `${BACKEND_URL}/api/team/inspections/management`;
const SCALE = { 1: 'Poor', 2: 'Needs Improvement', 3: 'Acceptable', 4: 'Very Good', 5: 'Excellent' };
const AREA_LABELS = {
  entrance: 'Entrance and lobby', teller: 'Teller area', offices: 'Offices', restrooms: 'Restrooms',
  break: 'Kitchen / break area', closet: 'Janitor closet', outside: 'Immediately outside entrance',
  atm: 'ATM area', drive: 'Teller drive-through', common: 'Common areas', classrooms: 'Classrooms',
  lounge: 'Staff lounge',
};
const AREA_ORDER = {
  bank: ['entrance', 'teller', 'offices', 'restrooms', 'break', 'closet', 'outside', 'atm', 'drive'],
  school: ['common', 'classrooms', 'offices', 'restrooms', 'lounge', 'closet'],
  office: ['entrance', 'offices', 'common', 'restrooms', 'closet'],
};
const date = (v) => (v ? new Date(v).toLocaleString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');

export default function OperationsTeamInspections() {
  const { token } = useContext(AuthContext);
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('submitted');
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [current, setCurrent] = useState(null);

  async function refresh() {
    try {
      const r = await axios.get(`${base}/list?status=${status}`, h);
      setRows(r.data); setLoaded(true); setError('');
    } catch { setError('Could not load field inspections. Your Operations access is required.'); }
  }
  useEffect(() => { refresh(); }, [token, status]); // eslint-disable-line

  const open = async (r) => {
    try { const d = await axios.get(`${base}/${r.id}`, h); setCurrent(d.data); } catch { setError('Could not open inspection.'); }
  };

  const photoUrl = (key) => `${BACKEND_URL}/api/cleaning-inspections/file?key=${encodeURIComponent(key)}&auth=${token}`;

  if (current) {
    const order = AREA_ORDER[current.type] || Object.keys(current.answers || {});
    return (
      <div className="mx-auto max-w-2xl space-y-4 p-4 md:p-6" data-testid="ops-team-insp-detail">
        <button onClick={() => setCurrent(null)} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#0E2A47]"><ArrowLeft size={15} /> Back</button>
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-[22px] font-extrabold text-[#0E2A47]">{current.inspection_number}</h1>
            <span className="rounded-full bg-[#8FC93A]/20 px-2.5 py-0.5 text-[11px] font-semibold text-[#4d7c0f]">{current.status === 'submitted' ? 'Submitted' : 'Draft'}</span>
          </div>
          <p className="mt-1 text-sm text-slate-500">{current.location_name} · {({ bank: 'Bank branch', school: 'School', office: 'Office building' })[current.type]} · {current.inspector_name || current.inspector} · {date(current.submitted_at || current.saved_at)}</p>
        </div>
        <div className="space-y-3">
          {order.map((k) => {
            const a = (current.answers || {})[k] || {};
            const tag = a.exclusion === 'not_applicable' ? 'Not applicable' : a.exclusion === 'not_inspected' ? 'Not inspected' : a.rating ? `${a.rating} · ${SCALE[a.rating]}` : 'Unanswered';
            return (
              <section key={k} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`ops-team-insp-area-${k}`}>
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-base font-semibold text-[#0E2A47]">{AREA_LABELS[k] || k}</h2>
                  <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${a.rating && a.rating <= 2 ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600'}`}>{tag}</span>
                </div>
                {a.note && <p className="mt-2 whitespace-pre-wrap break-words text-sm text-slate-600">{a.note}</p>}
                {(a.photos || []).length > 0 && (
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    {a.photos.map((p) => <a key={p.id} href={photoUrl(p.key)} target="_blank" rel="noreferrer" className="overflow-hidden rounded-xl border border-slate-200"><img src={photoUrl(p.key)} alt={p.name || 'photo'} className="h-24 w-full object-cover" /></a>)}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 p-4 md:p-6" data-testid="ops-team-insp-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-[#0E2A47]"><ClipboardCheck size={22} /> Field Inspections</h1>
          <p className="text-sm text-slate-600">Cleaner and technician area inspections submitted from the TEAM app.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="ops-team-insp-filter" className="min-h-11 rounded-lg border border-slate-300 px-3 text-sm font-semibold text-[#0E2A47]">
            <option value="submitted">Submitted</option>
            <option value="draft">In progress (drafts)</option>
            <option value="all">All</option>
          </select>
          <button onClick={refresh} className="min-h-11 rounded-lg border border-slate-300 px-4 text-sm font-semibold">Refresh</button>
        </div>
      </div>
      {error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-red-800">{error}</p>}
      {loaded && rows.length === 0 && <p className="rounded-xl border border-slate-200 bg-white p-6 text-center text-slate-500" data-testid="ops-team-insp-empty">No field inspections match this view.</p>}
      <div className="grid gap-2">
        {rows.map((r) => (
          <button key={r.id} onClick={() => open(r)} data-testid={`ops-team-insp-row-${r.id}`} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm hover:border-[#8FC93A]">
            <div>
              <div className="font-mono text-xs font-bold text-[#0E2A47]">{r.inspection_number}</div>
              <div className="mt-1 text-sm font-semibold text-[#0E2A47]">{r.location_name}</div>
              <div className="text-xs text-slate-500">{({ bank: 'Bank branch', school: 'School', office: 'Office building' })[r.type]} · {r.inspector_name || r.inspector} · {date(r.submitted_at || r.saved_at)}</div>
            </div>
            <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${r.status === 'submitted' ? 'bg-[#8FC93A]/20 text-[#4d7c0f]' : 'bg-amber-100 text-amber-700'}`}>{r.status === 'submitted' ? 'Submitted' : 'Draft'}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
